"use strict";
var __classPrivateFieldSet = (this && this.__classPrivateFieldSet) || function (receiver, privateMap, value) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to set private field on non-instance");
    }
    privateMap.set(receiver, value);
    return value;
};
var __classPrivateFieldGet = (this && this.__classPrivateFieldGet) || function (receiver, privateMap) {
    if (!privateMap.has(receiver)) {
        throw new TypeError("attempted to get private field on non-instance");
    }
    return privateMap.get(receiver);
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var _maxLength, _lastCallbacks, _overrunReporter, _callbacksMade, _underrunReporter;
Object.defineProperty(exports, "__esModule", { value: true });
exports.Reader = exports.Writer = exports.createAudioBuffer = exports.quantumSize = void 0;
const debug_1 = __importDefault(require("debug"));
const BufferConditionReporter_1 = require("./BufferConditionReporter");
const debugNamespace = 'vxt:rtc:sharedbuffer';
const debug = debug_1.default(debugNamespace);
// size of audio worklet quantum in samples/frames
exports.quantumSize = 128;
const channels = 2;
const sampleSize = 4; // in bytes
const headerLength = 12; // number of bytes in buffer before channel data
// representation of head and tail markers as 1-element typed arrays
function headArray(buffer) {
    return new Uint32Array(buffer, 4, 1);
}
function tailArray(buffer) {
    return new Uint32Array(buffer, 8, 1);
}
function createAudioBuffer(maxElems) {
    const bufferLength = maxElems + 1;
    let buffer = new SharedArrayBuffer(headerLength + bufferLength * channels * exports.quantumSize * sampleSize);
    new Uint32Array(buffer).fill(0);
    tailArray(buffer)[0] = maxElems; // buffer initially full and filled with zeros
    return { buffer: buffer, length: bufferLength };
}
exports.createAudioBuffer = createAudioBuffer;
// increment using 32-bit unsigned overflow arithmetic
function incrementMod32(i) {
    return (i + 1) | 0;
}
class BufferBaseHandler {
    // maximum length given in 128-sample quanta
    constructor(buf, reportCallback) {
        _maxLength.set(this, void 0);
        debug.enabled = debug.enabled || debug_1.default.enabled(debugNamespace);
        __classPrivateFieldSet(this, _maxLength, buf.length);
        this.buffer = buf.buffer;
        this.callbacks = new Uint32Array(this.buffer, 0, 1);
        this.head = headArray(this.buffer);
        this.tail = tailArray(this.buffer);
        this.reportCallback = reportCallback;
    }
    byteIndexForBuffer(quantum, channel) {
        return headerLength + (quantum * channels + channel) * exports.quantumSize * sampleSize;
    }
    // increments index in circular buffer, returns incremented value
    incrementIdx(idx) {
        return (idx + 1) % __classPrivateFieldGet(this, _maxLength);
    }
}
_maxLength = new WeakMap();
class Writer extends BufferBaseHandler {
    constructor(buf, reportCallback) {
        super(buf, reportCallback);
        _lastCallbacks.set(this, 0);
        _overrunReporter.set(this, void 0);
        __classPrivateFieldSet(this, _overrunReporter, new BufferConditionReporter_1.OverrunReporter(this.reportCallback, debug));
    }
    push(populate) {
        const callbacks = Atomics.load(this.callbacks, 0);
        try {
            for (let i = __classPrivateFieldGet(this, _lastCallbacks); i != callbacks; i = incrementMod32(i)) {
                const head = Atomics.load(this.head, 0);
                const tail = Atomics.load(this.tail, 0);
                const nextTail = this.incrementIdx(tail);
                if (nextTail === head) {
                    // buffer full
                    __classPrivateFieldGet(this, _overrunReporter).tick(true);
                    break;
                }
                __classPrivateFieldGet(this, _overrunReporter).tick(false);
                const buffersToPopulate = Array.from({ length: channels }, (v, i) => i).map((channel) => {
                    const byteIdx = this.byteIndexForBuffer(tail, channel);
                    return new Float32Array(this.buffer, byteIdx, exports.quantumSize);
                });
                populate(buffersToPopulate);
                Atomics.store(this.tail, 0, nextTail);
            }
        }
        finally {
            __classPrivateFieldSet(this, _lastCallbacks, callbacks);
        }
    }
}
exports.Writer = Writer;
_lastCallbacks = new WeakMap(), _overrunReporter = new WeakMap();
class Reader extends BufferBaseHandler {
    constructor(buf, reportCallback) {
        super(buf, reportCallback);
        _callbacksMade.set(this, 0);
        _underrunReporter.set(this, void 0);
        __classPrivateFieldSet(this, _underrunReporter, new BufferConditionReporter_1.UnderrunReporter(this.reportCallback, debug));
    }
    pull(destBuffers) {
        __classPrivateFieldSet(this, _callbacksMade, incrementMod32(__classPrivateFieldGet(this, _callbacksMade)));
        Atomics.store(this.callbacks, 0, __classPrivateFieldGet(this, _callbacksMade));
        const head = Atomics.load(this.head, 0);
        const tail = Atomics.load(this.tail, 0);
        const empty = tail === head;
        for (let channel = 0; channel < channels; channel++) {
            const dest = destBuffers[channel];
            if (empty) {
                // buffer empty, set destimation to 0
                __classPrivateFieldGet(this, _underrunReporter).tick(true);
                dest.fill(0);
            }
            else {
                __classPrivateFieldGet(this, _underrunReporter).tick(false);
                const byteIdx = this.byteIndexForBuffer(head, channel);
                const src = new Float32Array(this.buffer, byteIdx, exports.quantumSize);
                dest.set(src);
            }
        }
        if (!empty) {
            Atomics.store(this.head, 0, this.incrementIdx(head));
        }
    }
}
exports.Reader = Reader;
_callbacksMade = new WeakMap(), _underrunReporter = new WeakMap();
